const state = () => ({
   rawData: null,
   loaded: false,
   studySetupData: {},
});

/** Action types used by the server. */
const ServerActionTypes = Object.freeze({
   /**
    * ServerActionTypes.TIME_SURVEY_DATA
    * Indicates how much of a user's assigned employees have completed data
    * data:
    *    completed - Timestamp of when the questionnaire was marked complete or null
    *    uid       - User ID or null
    *    firstName - User's first name or null
    *    lastName  - User's last name or null
    *    units     - Total number of assigned employees
    *    completed - The number of assigned employees with complete data
    *    speriodId      - The periodId of the time survey
    */
   TIME_SURVEY_DATA: 'TIME_SURVEY_DATA',

   /**
    * ServerActionTypes.TIME_SURVEY_SUBMIT
    * Indicates that a time survey is ready for submission
    * data:
    *    speriodId - The periodId of the time survey
    */
   TIME_SURVEY_SUBMIT: 'TIME_SURVEY_SUBMIT',

   /**
    * ServerActionTypes.TIME_SURVEY_MARK_COMPLETED
    * Indicates that a user is ready to mark their assigned employees complete in a time survey
    * data:
    *    completed - The number of employees with complete, valid data assigned to the user
    *    firstName - User's first name
    *    lastName  - User's last name
    *    marked    - Number of employees marked complete
    *    uid       - User ID
    *    units     - Number of units of progress possible
    *    speriodId      - periodId of the time survey
    */
   TIME_SURVEY_MARK_COMPLETED: 'TIME_SURVEY_MARK_COMPLETED',

   /**
    * ServerActionTypes.TIME_SURVEY_MARK_COMPLETED
    * Indicates that a user is ready to mark their assigned contractors complete in a contract research
    * data:
    *    completed - The number of contractors with complete, valid data assigned to the user
    *    firstName - User's first name
    *    lastName  - User's last name
    *    marked    - Number of contractors marked complete
    *    uid       - User ID
    *    units     - Number of units of progress possible
    *    speriodId      - periodId of the contract research
    */
   CONTRACTOR_MARK_COMPLETED: 'CONTRACTOR_MARK_COMPLETED',

   /**
    * ServerActionTypes.CONTRACTOR_DATA
    * Indicates how much of a user's assigned contractors have completed data
    * data:
    *    completed - The number of contractors with complete, valid data assigned to the user
    *    uid       - User ID or null
    *    firstName - User's first name or null
    *    lastName  - User's last name or null
    *    units     - Total number of assigned employees
    *    completed - The number of assigned contractors with complete data
    *    speriodId      - The periodId of the time survey
    */
   CONTRACTOR_DATA: 'CONTRACTOR_DATA',

   /**
    * ServerActionTypes.CONTRACTOR_INVOICE_DATA
    * Indicates how much of a user's assigned contractors invoice data have completed data
    * data:
    *    completed - The number of contractors invoice data with complete, valid data assigned to the user
    *    uid       - User ID or null
    *    firstName - User's first name or null
    *    lastName  - User's last name or null
    *    units     - Total number of assigned employees
    *    completed - The number of assigned contractors with complete data
    *    speriodId      - The periodId of the time survey
    */
   CONTRACTOR_INVOICE_DATA: 'CONTRACTOR_INVOICE_DATA',

   /**
    * ServerActionTypes.PROJECT_QUESTIONNAIRE_ANSWERS
    * Indicates how many questions of a project questionnaire are unanswered
    * data:
    *    completed   - Timestamp of when the questionnaire was marked complete or null
    *    count       - Total number of questions in the questionnaire
    *    unanswered  - An array of question ids for questions that are unanswered
    *    id          - The id of the questionnaire
    *    title       - The title of the questionnaire
    *    speriodId        - The periodId of the questionnaire
    *    projectId   - The id of the project
    *    projectName - The name of the project
    */
   PROJECT_QUESTIONNAIRE_ANSWERS: 'PROJECT_QUESTIONNAIRE_ANSWERS',

   /**
    * ServerActionTypes.COMPANY_QUESTION_ANSWERS
    * Indicates the number of questions of a company questionnaire are unanswered
    * data:
    *    completed  - Timestamp of when the questionnaire was marked complete or null
    *    count      - Total number of questions in the questionnaire
    *    unanswered - An array of question ids for questions that are unanswered
    *    id         - The id of the questionnaire
    *    title      - The title of the questionnaire
    *    speriodId       - The periodId of the questionnaire
    */
   COMPANY_QUESTIONNAIRE_ANSWERS: 'COMPANY_QUESTIONNAIRE_ANSWERS',

   /**
    * ServerActionTypes.PROFILE_QUESTION_ANSWERS
    * Indicates the number of questions of a profile questionnaire are unanswered
    * data:
    *    completed  - Timestamp of when the questionnaire was marked complete or null
    *    count      - Total number of questions in the questionnaire
    *    unanswered - An array of question ids for questions that are unanswered
    *    id         - The id of the questionnaire
    *    title      - The title of the questionnaire
    *    speriodId       - The periodId of the questionnaire
    */
   PROFILE_QUESTIONNAIRE_ANSWERS: 'PROFILE_QUESTIONNAIRE_ANSWERS',

   /**
    * ServerActionTypes.CONTRACTOR
    * Indicates that some contractors have incomplete basic info
    * data:
    *    invalid - Incomplete or invalid data per contractor
    */
   CONTRACTOR: 'CONTRACTOR',

   /**
    * ServerActionTypes.CONTRACTOR_SUBMIT
    * Indicates that the contractor section is ready to submit
    * no data
    */
   CONTRACTOR_SUBMIT: 'CONTRACTOR_SUBMIT',

   /**
    * ServerActionTypes.GROSS_RECEIPTS_MISSING
    * Indicates that the user needs to provide tax return data
    * data:
    *  units - Missing tax return data
    *  completed - Total tax return data needed
    */

   /**
    * ServerActionTypes.CONTRACTOR_SECTION_READY
    * Indicates that the contractor section is ready for submission
    * data:
    *    speriodId - The periodId of the contractor section
    */
   CONTRACTOR_SECTION_READY: 'CONTRACTOR_SECTION_READY',

   /**
    * ServerActionTypes.QUESTIONNAIRE_SECTION_READY
    * Indicates every questionnaire in the study is individually completed,
    * so the Questionnaires section is ready to be locked (Mark Section
    * Complete).
    * data:
    *    uid       - User ID
    *    firstName - User's first name
    *    lastName  - User's last name
    */
   QUESTIONNAIRE_SECTION_READY: 'QUESTIONNAIRE_SECTION_READY',

   /**
    * ServerActionTypes.QUESTIONNAIRE_WAITING_ON_PROJECT
    * Indicates every Company questionnaire is individually completed, but
    * the section isn't ready to lock because Project questionnaires are
    * still outstanding.
    * data:
    *    uid       - User ID
    *    firstName - User's first name
    *    lastName  - User's last name
    */
   QUESTIONNAIRE_WAITING_ON_PROJECT: 'QUESTIONNAIRE_WAITING_ON_PROJECT',

   /**
    * ServerActionTypes.QUESTIONNAIRE_WAITING_ON_COMPANY
    * Indicates every Project questionnaire is individually completed, but
    * the section isn't ready to lock because Company questionnaires are
    * still outstanding.
    * data:
    *    uid       - User ID
    *    firstName - User's first name
    *    lastName  - User's last name
    */
   QUESTIONNAIRE_WAITING_ON_COMPANY: 'QUESTIONNAIRE_WAITING_ON_COMPANY',

   GROSS_RECEIPTS_MISSING: 'GROSS_RECEIPTS_MISSING',

   /**
    * ServerActionTypes.PAYROLL_DATA
    * Indicates that the user needs to provide payroll data
    * data:
    *  total - Total employees payroll data needed
    *  missing - Missing payroll data
    */
   PAYROLL_DATA: 'PAYROLL_DATA',

   /**
    * ServerActionTypes.SUPPLIES_DATA
    * Indicates how much of a user's assigned supplies vendors have completed data
    * data:
    *    uid       - User ID or null
    *    firstName - User's first name or null
    *    lastName  - User's last name or null
    *    units     - Total number of assigned vendors
    *    completed - The number of assigned vendors with complete data
    *    speriodId      - The periodId of the time survey
    */
   SUPPLIES_DATA: 'SUPPLIES_DATA',
   /**
    * ServerActionTypes.SUPPLIES_SUBMIT
    * Indicates that a time survey is ready for submission
    * data:
    *    speriodId - The periodId of the time survey
    */
   SUPPLIES_SUBMIT: 'SUPPLIES_SUBMIT',
   /**
    * ServerActionTypes.SUPPLIES_MARK_COMPLETED
    * Indicates that a user is ready to mark their assigned vendors complete in a time survey
    * data:
    *    completed - The number of vendors with complete, valid data assigned to the user
    *    firstName - User's first name
    *    lastName  - User's last name
    *    marked    - Number of vendors marked complete
    *    uid       - User ID
    *    units     - Number of units of progress possible
    *    speriodId      - periodId of the time survey
    */
   SUPPLIES_MARK_COMPLETED: 'SUPPLIES_MARK_COMPLETED',
   /**
    * ServerActionTypes.CLOUD_DATA
    * Indicates how much of a user's assigned supples vendors have completed data
    * data:
    *    uid       - User ID or null
    *    firstName - User's first name or null
    *    lastName  - User's last name or null
    *    units     - Total number of assigned vendors
    *    completed - The number of assigned vendors with complete data
    *    speriodId      - The periodId of the time survey
    */
   CLOUD_DATA: 'CLOUD_DATA',
   /**
    * ServerActionTypes.CLOUD_SUBMIT
    * Indicates that a time survey is ready for submission
    * data:
    *    speriodId - The periodId of the time survey
    */
   CLOUD_SUBMIT: 'CLOUD_SUBMIT',
   /**
    * ServerActionTypes.CLOUD_MARK_COMPLETED
    * Indicates that a user is ready to mark their assigned vendors complete in a time survey
    * data:
    *    completed - The number of vendors with complete, valid data assigned to the user
    *    firstName - User's first name
    *    lastName  - User's last name
    *    marked    - Number of vendors marked complete
    *    uid       - User ID
    *    units     - Number of units of progress possible
    *    speriodId      - periodId of the time survey
    */
   CLOUD_MARK_COMPLETED: 'CLOUD_MARK_COMPLETED',
});

/** Action types used by this app. */
const ActionItemTypes = Object.freeze({
   /**
    * ActionItemTypes.TIME_SURVEY_DATA
    * Indicates that a user's assigned employees have incomplete data
    * parent:
    *    ServerActionTypes.TIME_SURVEY_DATA
    */
   TIME_SURVEY_DATA: 'TIME_SURVEY_DATA',

   /**
    * ActionItemTypes.TIME_SURVEY_ASSIGNMENTS
    * Indicates that a user is responsible for assigning employees
    * parent:
    *    ServerActionTypes.TIME_SURVEY_DATA
    */
   TIME_SURVEY_ASSIGNMENTS: 'TIME_SURVEY_ASSIGNMENTS',

   /**
    * ActionItemTypes.TIME_SURVEY_SUBMIT
    * Indicates that a time survey is ready for submission
    * parent:
    *    ServerActionTypes.TIME_SURVEY_SUBMIT
    */
   TIME_SURVEY_SUBMIT: 'TIME_SURVEY_SUBMIT',

   /**
    * ActionItemTypes.TIME_SURVEY_DATA_FINISHED
    * Indicates that a user has completed entering data for their assigned employees
    * parent:
    *    ServerActionTypes.TIME_SURVEY_DATA
    */
   TIME_SURVEY_DATA_FINISHED: 'TIME_SURVEY_DATA_FINISHED',

   /**
    * ActionItemTypes.TIME_SURVEY_MARK_COMPLETED
    * Indicates that user is ready to mark their assigned employees complete
    * parent:
    *    ServerActionTypes.TIME_SURVEY_MARK_COMPLETED
    */
   TIME_SURVEY_MARK_COMPLETED: 'TIME_SURVEY_MARK_COMPLETED',

   CONTRACTOR_MARK_COMPLETED: 'CONTRACTOR_MARK_COMPLETED',

   CONTRACTOR_DATA: 'CONTRACTOR_DATA',

   CONTRACTOR_DATA_FINISHED: 'CONTRACTOR_DATA_FINISHED',

   CONTRACTOR_ASSIGNMENTS: 'CONTRACTOR_ASSIGNMENTS',

   CONTRACTOR_INVOICE_DATA: 'CONTRACTOR_INVOICE_DATA',

   CONTRACTOR_INVOICE_DATA_FINISHED: 'CONTRACTOR_INVOICE_DATA_FINISHED',

   CONTRACTOR_INVOICE_ASSIGNMENTS: 'CONTRACTOR_INVOICE_ASSIGNMENTS',

   /**
    * ActionItemTypes.PROJECT_QUESTIONNAIRE_ANSWERS
    * Indicates that a questionnaire has unanswered questions
    * parent:
    *    ServerActionTypes.PROJECT_QUESTIONNAIRE_ANSWERS
    */
   PROJECT_QUESTIONNAIRE_ANSWERS: 'PROJECT_QUESTIONNAIRE_ANSWERS',

   /**
    * ActionItemTypes.PROJECT_QUESTIONNAIRE_SUBMIT
    * Indicates that a questionnaire has no unanswered questions, but has not been submitted
    * parent:
    *    ServerActionTypes.PROJECT_QUESTIONNAIRE_ANSWERS
    */
   PROJECT_QUESTIONNAIRE_SUBMIT: 'PROJECT_QUESTIONNAIRE_SUBMIT',

   /**
    * ActionItemTypes.PROJECT_QUESTIONNAIRE_FINISHED
    * Indicates that a questionnaire has been submitted
    * parent:
    *    ServerActionTypes.PROJECT_QUESTIONNAIRE_ANSWERS
    */
   PROJECT_QUESTIONNAIRE_FINISHED: 'PROJECT_QUESTIONNAIRE_FINISHED',

   /**
    * ActionItemTypes.COMPANY_QUESTION_ANSWERS
    * Indicates that a questionnaire has unanswered questions
    * parent:
    *    ServerActionTypes.COMPANY_QUESTIONNAIRE_ANSWERS
    */
   COMPANY_QUESTIONNAIRE_ANSWERS: 'COMPANY_QUESTIONNAIRE_ANSWERS',

   /**
    * ActionItemTypes.COMPANY_QUESTIONNAIRE_SUBMIT
    * Indicates that a questionnaire has no unanswered questions, but has not been submitted
    * parent:
    *    ServerActionTypes.COMPANY_QUESTIONNAIRE_ANSWERS
    */
   COMPANY_QUESTIONNAIRE_SUBMIT: 'COMPANY_QUESTIONNAIRE_SUBMIT',

   /**
    * ActionItemTypes.COMPANY_QUESTIONNAIRE_FINISHED
    * Indicates that a questionnaire has been submitted
    * parent:
    *    ServerActionTypes.COMPANY_QUESTIONNAIRE_ANSWERS
    */
   COMPANY_QUESTIONNAIRE_FINISHED: 'COMPANY_QUESTIONNAIRE_FINISHED',

   /**
    * ActionItemTypes.PROFILE_QUESTION_ANSWERS
    * Indicates that a questionnaire has unanswered questions
    * parent:
    *    ServerActionTypes.PROFILE_QUESTIONNAIRE_ANSWERS
    */
   PROFILE_QUESTIONNAIRE_ANSWERS: 'PROFILE_QUESTIONNAIRE_ANSWERS',

   /**
    * ActionItemTypes.PROFILE_QUESTIONNAIRE_FINISHED
    * Indicates that all profile questionnaire questions have been answered
    * parent:
    *    ServerActionTypes.PROFILE_QUESTIONNAIRE_ANSWERS
    */
   PROFILE_QUESTIONNAIRE_FINISHED: 'PROFILE_QUESTIONNAIRE_FINISHED',

   /**
    * ActionItemTypes.SUPPLIES_DECLARATION
    * Indicates that the user needs to make a supplies declaration
    */
   SUPPLIES_DECLARATION: 'SUPPLIES_DECLARATION',

   /**
    * ActionItemTypes.SUPPLIES_UPLOAD
    * Indicates that the user needs to upload supplies data
    */
   SUPPLIES_UPLOAD: 'SUPPLIES_UPLOAD',

   /**
    * ActionItemTypes.SUPPLIES_DATA
    * Indicates that the user needs to enter supplies data
    */
   SUPPLIES_DATA: 'SUPPLIES_DATA',

   /**
    * ActionItemTypes.SUPPLIES_SUBMIT
    * Indicates that the user needs to submit supplies data
    */
   SUPPLIES_SUBMIT: 'SUPPLIES_SUBMIT',

   /**
    * ActionItemTypes.SUPPLIES_COMPLETE
    * Indicates that the supplies section is complete
    */
   SUPPLIES_COMPLETE: 'SUPPLIES_COMPLETE',
   SUPPLIES_MARK_COMPLETED: 'SUPPLIES_MARK_COMPLETED',
   SUPPLIES_DATA_FINISHED: 'SUPPLIES_DATA_FINISHED',
   SUPPLIES_ASSIGNMENTS: 'SUPPLIES_ASSIGNMENTS',

   /**
    * ActionItemTypes.CONTRACTOR_EMPTY
    * Indicates that no contractors have been created
    */
   CONTRACTOR_EMPTY: 'CONTRACTOR_EMPTY',

   /**
    * ActionItemTypes.CONTRACTOR_INCOMPLETE
    * Indicates that some contractors have incomplete data
    */
   CONTRACTOR_INCOMPLETE: 'CONTRACTOR_INCOMPLETE',

   /**
    * ActionItemTypes.CONTRACTOR_SUBMIT
    * Indicates that the contractor section is ready to submit
    */
   CONTRACTOR_SUBMIT: 'CONTRACTOR_SUBMIT',

   /**
    * ActionItemTypes.CONTRACTOR_COMPLETE
    * Indicates that the contractor section is complete
    */
   CONTRACTOR_COMPLETE: 'CONTRACTOR_COMPLETE',

   /**
    * ActionTypes.CONTRACTOR_SECTION_READY
    * Indicates that the contractor section is ready for submission
    * data:
    *    speriodId - The periodId of the contractor section
    */
   CONTRACTOR_SECTION_READY: 'CONTRACTOR_SECTION_READY',

   /**
    * ActionItemTypes.QUESTIONNAIRE_SECTION_READY
    * Indicates that the Questionnaires section is ready to be locked
    */
   QUESTIONNAIRE_SECTION_READY: 'QUESTIONNAIRE_SECTION_READY',

   /**
    * ActionItemTypes.QUESTIONNAIRE_WAITING_ON_PROJECT
    * Indicates Company questionnaires are all complete, but the section
    * can't be marked ready until Project questionnaires are too
    */
   QUESTIONNAIRE_WAITING_ON_PROJECT: 'QUESTIONNAIRE_WAITING_ON_PROJECT',

   /**
    * ActionItemTypes.QUESTIONNAIRE_WAITING_ON_COMPANY
    * Indicates Project questionnaires are all complete, but the section
    * can't be marked ready until Company questionnaires are too
    */
   QUESTIONNAIRE_WAITING_ON_COMPANY: 'QUESTIONNAIRE_WAITING_ON_COMPANY',

   /**
    * ActionItemTypes.CLOUD_EMPTY
    * Indicates that no cloud vendors have been created
    */
   CLOUD_EMPTY: 'CLOUD_EMPTY',

   /**
    * ActionItemTypes.CLOUD_UPLOAD
    * Indicates that the user needs to upload cloud computing data
    */
   CLOUD_UPLOAD: 'CLOUD_UPLOAD',

   /**
    * ActionItemTypes.CLOUD_DATA
    * Indicates that the user needs to fill-in missing cloud computing data
    */
   CLOUD_DATA: 'CLOUD_DATA',

   /**
    * ActionItemTypes.CLOUD_SUBMIT
    * Indicates that the cloud computing section is ready to submit
    */
   CLOUD_SUBMIT: 'CLOUD_SUBMIT',

   /**
    * ActionItemTypes.CLOUD_COMPLETE
    * Indicates that the cloud computing section is complete
    */
   CLOUD_COMPLETE: 'CLOUD_COMPLETE',
   CLOUD_MARK_COMPLETED: 'CLOUD_MARK_COMPLETED',
   CLOUD_DATA_FINISHED: 'CLOUD_DATA_FINISHED',
   CLOUD_ASSIGNMENTS: 'CLOUD_ASSIGNMENTS',

   /**
    * ActionItemTypes.DOCUMENTS
    * Indicates that the user needs to provide requested documents
    */
   DOCUMENTS: 'DOCUMENTS',

   /**
    * ActionItemTypes.GROSS_RECEIPTS_MISSING
    * Indicates that the user needs to provide tax return data
    */
   GROSS_RECEIPTS_MISSING: 'GROSS_RECEIPTS_MISSING',

   /**
    * ActionItemTypes.GROSS_RECEIPTS_FINISHED
    * Indicates that the user has provided necessary tax return data
    */
   GROSS_RECEIPTS_FINISHED: 'GROSS_RECEIPTS_FINISHED',

   /**
    * ActionItemTypes.PAYROLL_DATA_FINISHED
    * Indicates that the user has provided necessary payroll data
    */
   PAYROLL_DATA_FINISHED: 'PAYROLL_DATA_FINISHED',

   /**
    * ActionItemTypes.PAYROLL_DATA_MISSING
    * Indicates that the user needs to provide payroll data
    */
   PAYROLL_DATA_MISSING: 'PAYROLL_DATA_MISSING',
});

/**
 * A mapping of action item types to that action item's
 * priority as an integer. Smaller number => more important.
 *
 * Priority follows sidebar order (see stages.js): within each section,
 * items ready to submit rank above incomplete work, which ranks above
 * completed work. Sections are NOT interleaved by state - e.g. an
 * incomplete Employee Time item always outranks a ready-to-submit
 * Questionnaire item, because Employee Time comes first in the sidebar.
 */
const ACTION_PRIORITY = [
   // EMPLOYEE TIME
   ActionItemTypes.TIME_SURVEY_SUBMIT,
   ActionItemTypes.TIME_SURVEY_MARK_COMPLETED,
   ActionItemTypes.TIME_SURVEY_DATA,
   ActionItemTypes.TIME_SURVEY_ASSIGNMENTS,
   ActionItemTypes.TIME_SURVEY_DATA_FINISHED,

   // CONTRACT RESEARCH
   ActionItemTypes.CONTRACTOR_SUBMIT,
   ActionItemTypes.CONTRACTOR_MARK_COMPLETED,
   ActionItemTypes.CONTRACTOR_INVOICE_DATA,
   ActionItemTypes.CONTRACTOR_DATA,
   ActionItemTypes.CONTRACTOR_ASSIGNMENTS,
   ActionItemTypes.CONTRACTOR_INVOICE_ASSIGNMENTS,
   ActionItemTypes.CONTRACTOR_EMPTY,
   ActionItemTypes.CONTRACTOR_INCOMPLETE,
   ActionItemTypes.CONTRACTOR_COMPLETE,
   ActionItemTypes.CONTRACTOR_DATA_FINISHED,
   ActionItemTypes.CONTRACTOR_INVOICE_DATA_FINISHED,

   // SUPPLIES
   ActionItemTypes.SUPPLIES_SUBMIT,
   ActionItemTypes.SUPPLIES_MARK_COMPLETED,
   ActionItemTypes.SUPPLIES_DECLARATION,
   ActionItemTypes.SUPPLIES_UPLOAD,
   ActionItemTypes.SUPPLIES_DATA,
   ActionItemTypes.SUPPLIES_ASSIGNMENTS,
   ActionItemTypes.SUPPLIES_COMPLETE,
   ActionItemTypes.SUPPLIES_DATA_FINISHED,

   // CLOUD COMPUTING
   ActionItemTypes.CLOUD_SUBMIT,
   ActionItemTypes.CLOUD_MARK_COMPLETED,
   ActionItemTypes.CLOUD_EMPTY,
   ActionItemTypes.CLOUD_UPLOAD,
   ActionItemTypes.CLOUD_DATA,
   ActionItemTypes.CLOUD_ASSIGNMENTS,
   ActionItemTypes.CLOUD_COMPLETE,
   ActionItemTypes.CLOUD_DATA_FINISHED,

   // QUESTIONNAIRES
   ActionItemTypes.COMPANY_QUESTIONNAIRE_SUBMIT,
   ActionItemTypes.PROJECT_QUESTIONNAIRE_SUBMIT,
   ActionItemTypes.COMPANY_QUESTIONNAIRE_ANSWERS,
   ActionItemTypes.PROJECT_QUESTIONNAIRE_ANSWERS,
   ActionItemTypes.COMPANY_QUESTIONNAIRE_FINISHED,
   ActionItemTypes.PROJECT_QUESTIONNAIRE_FINISHED,
   // Lowest priority of the questionnaire items -- and lower priority than
   // Employee Time/Contract Research/Supplies/Cloud Computing's own
   // section-ready items -- since it's purely a "you can now lock the
   // section" nudge, not outstanding work.
   ActionItemTypes.QUESTIONNAIRE_SECTION_READY,
   // Even lower priority -- purely informational ("nothing to do until the
   // other bucket catches up"), not a nudge to take action.
   ActionItemTypes.QUESTIONNAIRE_WAITING_ON_PROJECT,
   ActionItemTypes.QUESTIONNAIRE_WAITING_ON_COMPANY,

   // STUDY SETUP (tax return / payroll data, documents)
   ActionItemTypes.GROSS_RECEIPTS_MISSING,
   ActionItemTypes.PAYROLL_DATA,
   ActionItemTypes.PAYROLL_DATA_MISSING,
   ActionItemTypes.DOCUMENTS,
   ActionItemTypes.GROSS_RECEIPTS_FINISHED,
   ActionItemTypes.PAYROLL_DATA_FINISHED,

   // PROFILE QUESTIONS LAST
   ActionItemTypes.PROFILE_QUESTIONNAIRE_FINISHED,
   ActionItemTypes.PROFILE_QUESTIONNAIRE_ANSWERS,
].reduce((obj, action, idx) => ({...obj, [action]: idx}), {});

const CLOUD_CONFIG = {type: 'CLOUD', label: 'Cloud Computing', routeName: 'cloud-period-review'};
const SUPPLIES_CONFIG = {type: 'SUPPLIES', label: 'Supplies', routeName: 'supplies-period-review'};
const EMPLOYEE_CONFIG = {
   type: 'TIME_SURVEY',
   label: 'Employee Time',
   routeName: 'employees-period-review',
};
const expenseDataFormatter = (item, profile, periodMap, studyId, config) => {
   const {type, label, routeName} = config;
   const incomplete = item.units - item.completed;
   const userName = item.uid ? `${item.firstName} ${item.lastName}` : 'Unassigned';
   const period = periodMap[item.speriodId];

   let action, text, to, buttonText;
   const reviewRoute = {name: routeName, params: {periodId: period.id}};

   if (incomplete === 0) {
      action = ActionItemTypes[`${type}_DATA_FINISHED`];
      text = `${userName} - All${item.uid ? ' assigned' : ''} ${
         period.label
      } vendors have valid data`;
      to = reviewRoute;
      buttonText = 'Review';
   } else if (item.uid === null) {
      action = ActionItemTypes[`${type}_ASSIGNMENTS`];
      to = reviewRoute;
      buttonText = 'Review Assignments';
      text = `Some vendors need assignments for ${label} time entry ${period.label}`;
   } else {
      action = ActionItemTypes[`${type}_DATA`];
      to = reviewRoute;

      if (item.uid === profile.id) {
         buttonText = incomplete < item.units ? 'Go' : 'Start';
         text = `${incomplete}/${item.units} ${label} vendor${
            incomplete === 1 ? '' : 's'
         } assigned to you need${incomplete === 1 ? 's' : ''} time entered for ${period.label}`;
      } else {
         buttonText = 'Review Assignments';
         text = `${userName} - ${incomplete}/${item.units} vendors remaining in ${period.label} ${label}`;
      }
   }

   return {action, data: item, text, to, buttonText, studyId};
};
const expenseCompletionFormatter = (item, profile, periodMap, studyId, config) => {
   const {type, label, routeName} = config;
   const period = periodMap[item.speriodId];
   const userName = item.uid ? `${item.firstName} ${item.lastName}` : 'Unassigned';
   const text = `${userName} - Awaiting completion of ${period.label} ${label}`;

   return {
      action: ActionItemTypes[`${type}_MARK_COMPLETED`],
      data: item,
      text,
      to: {name: routeName, params: {periodId: period.id}},
      buttonText: item.uid === profile.id ? 'Go' : 'Review Assignments',
      studyId,
   };
};
const expenseSubmitFormatter = (item, periodMap, studyId, config) => {
   const {type, label, routeName} = config;

   return {
      action: ActionItemTypes[`${type}_SUBMIT`],
      data: item,
      text: `${label} section is ready to be reviewed and marked complete`,
      to: {name: routeName},
      buttonText: 'Go',
      studyId,
   };
};
/**
 * Formatters for incoming action items
 * {
 *    action     - an ActionItemType
 *    text       - A text description of the action
 *    to         - A route to direct the user to
 *    buttonText - Text for the button attached to the action
 *    data       - The incoming data associated with the action
 * }
 */
const ActionItemFormatters = {
   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.TIME_SURVEY_DATA]: (item, profile, periodMap, studyId) => {
      const incomplete = item.units - item.completed;
      const userName = item.uid ? `${item.firstName} ${item.lastName}` : 'Unassigned';
      const period = periodMap[item.speriodId];

      let action, text, to, buttonText;
      if (incomplete === 0) {
         action = ActionItemTypes.TIME_SURVEY_DATA_FINISHED;
         text = `${userName} - All${item.uid ? ' assigned' : ''} ${
            period.label
         } employees have valid data`;
         to = {name: EMPLOYEE_CONFIG.routeName, params: {periodId: period.id}};
         buttonText = 'Review';
      } else if (item.uid === null) {
         action = ActionItemTypes.TIME_SURVEY_ASSIGNMENTS;
         to = {name: 'employees-table', params: {periodId: period.id}};
         buttonText = 'Review Assignments';
         text = `Some employees need assignments for employee time entry ${period.label}`;
      } else {
         action = ActionItemTypes.TIME_SURVEY_DATA;

         if (item.uid === profile.id) {
            // Time survey employees assigned to the current user with incomplete data
            to = {name: EMPLOYEE_CONFIG.routeName, params: {periodId: period.id}};
            buttonText = incomplete < item.units ? 'Go' : 'Start';
            text = `${incomplete}/${item.units} employee${
               incomplete === 1 ? '' : 's'
            } assigned to you need${incomplete === 1 ? 's' : ''} time entered for ${period.label}`;
         } else {
            // Time survey employees assigned to another user with incomplete data
            to = {name: EMPLOYEE_CONFIG.routeName, params: {periodId: period.id}};
            buttonText = 'Review Assignments';
            text = `${userName} - ${incomplete}/${item.units} employees remaining in ${period.label} Employee Time`;
         }
      }

      return {action, data: item, text, to, buttonText, studyId};
   },
   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.CLOUD_DATA]: (item, profile, periodMap, studyId) =>
      expenseDataFormatter(item, profile, periodMap, studyId, CLOUD_CONFIG),

   [ServerActionTypes.SUPPLIES_DATA]: (item, profile, periodMap, studyId) =>
      expenseDataFormatter(item, profile, periodMap, studyId, SUPPLIES_CONFIG),

   [ServerActionTypes.CONTRACTOR_DATA]: (item, profile, periodMap, studyId) => {
      const incomplete = item.units - item.completed;
      const userName = item.uid ? `${item.firstName} ${item.lastName}` : 'Unassigned';
      const period = periodMap[item.speriodId];

      let action, text, to, buttonText;
      if (incomplete === 0) {
         action = ActionItemTypes.CONTRACTOR_DATA_FINISHED;
         text = `${userName} - All${item.uid ? ' assigned' : ''} ${
            period.label
         } contractors have valid data`;
         to = {name: 'contractors-period-review', params: {periodId: period.id}};
         buttonText = 'Review';
      } else if (item.uid === null) {
         action = ActionItemTypes.CONTRACTOR_ASSIGNMENTS;
         to = {name: 'contractors-table', params: {periodId: period.id}};
         buttonText = 'Review Assignments';
         text = `Some contract researchers need assignments for contract research time entry`;
      } else {
         action = ActionItemTypes.CONTRACTOR_DATA;
         to = {name: 'contractors-period-review', params: {periodId: period.id}};
         if (item.uid === profile.id) {
            // Contract research contractors assigned to the current user with incomplete data
            buttonText = 'Go';
            text = `${incomplete}/${item.units} contract researcher${
               incomplete === 1 ? '' : 's'
            } assigned to you need${incomplete === 1 ? 's' : ''} time entered for ${period.label}`;
         } else {
            // Contract Research contractors assigned to another user with incomplete data
            buttonText = 'Review Assignments';
            text = `${userName} - ${incomplete}/${item.units} contract researchers remaining in ${period.label} Contract Research`;
         }
      }

      return {action, data: item, text, to, buttonText, studyId};
   },

   [ServerActionTypes.CONTRACTOR_INVOICE_DATA]: (item, profile, periodMap, studyId) => {
      const incomplete = item.units - item.filledPayments;
      const period = periodMap[item.speriodId];

      let action, text, to, buttonText;
      if (incomplete === 0) {
         action = ActionItemTypes.CONTRACTOR_INVOICE_DATA_FINISHED;
         text = `All contract researchers have payment data for ${period.label}`;
         to = {name: 'contractors-period-review', params: {periodId: period.id}};
      } else {
         action = ActionItemTypes.CONTRACTOR_INVOICE_DATA;
         to = {
            name: 'contractors-period-review',
            params: {periodId: period.id, shouldOpenPaymentModal: true},
         };
         buttonText = 'Go';

         text = `${incomplete}/${item.units} contract researchers need payments entered for ${period.label}`;
      }

      return {action, data: item, text, to, buttonText, studyId};
   },

   [ServerActionTypes.TIME_SURVEY_MARK_COMPLETED]: (item, profile, periodMap, studyId) => {
      return {
         ...expenseCompletionFormatter(item, profile, periodMap, studyId, EMPLOYEE_CONFIG),
         action: ActionItemTypes.TIME_SURVEY_MARK_COMPLETED,
      };
   },

   [ServerActionTypes.CONTRACTOR_MARK_COMPLETED]: (item, profile, periodMap, studyId) => {
      let to, buttonText;
      const period = periodMap[item.speriodId];
      const userName = item.uid ? `${item.firstName} ${item.lastName}` : 'Unassigned';
      const text = `${userName} - Awaiting completion of ${period.label} Contract Research`;

      if (item.uid === profile.id) {
         to = {name: 'contractors-period-review', params: {periodId: period.id}};
         buttonText = 'Go';
      } else {
         to = {name: 'contractors-table', params: {periodId: period.id}};
         buttonText = 'Review Assignments';
      }
      return {
         action: ActionItemTypes.CONTRACTOR_MARK_COMPLETED,
         data: item,
         text,
         to,
         buttonText,
         studyId,
      };
   },
   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.CONTRACTOR_SECTION_READY]: (item, profile, periodMap, studyId) => {
      const {label, routeName} = {
         type: 'CONTRACTOR',
         label: 'Contract Research',
         routeName: 'contractors-table',
      };

      return {
         action: ActionItemTypes[`CONTRACTOR_DATA`],
         data: item,
         text: `${label} section is ready to be reviewed and marked complete`,
         to: {name: routeName},
         buttonText: 'Go',
         studyId,
      };
   },
   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.QUESTIONNAIRE_SECTION_READY]: (item, profile, periodMap, studyId) => {
      return {
         action: ActionItemTypes.QUESTIONNAIRE_SECTION_READY,
         data: item,
         text: 'Questionnaire section is ready to be reviewed and marked complete',
         to: {name: 'questionnaires'},
         buttonText: 'Go',
         studyId,
      };
   },
   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.QUESTIONNAIRE_WAITING_ON_PROJECT]: (item, profile, periodMap, studyId) => {
      return {
         action: ActionItemTypes.QUESTIONNAIRE_WAITING_ON_PROJECT,
         data: item,
         text: 'Waiting on Project Questionnaires completion',
         to: {name: 'questionnaires'},
         buttonText: 'Go',
         studyId,
      };
   },
   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.QUESTIONNAIRE_WAITING_ON_COMPANY]: (item, profile, periodMap, studyId) => {
      return {
         action: ActionItemTypes.QUESTIONNAIRE_WAITING_ON_COMPANY,
         data: item,
         text: 'Waiting on Company Questionnaires completion',
         to: {name: 'questionnaires'},
         buttonText: 'Go',
         studyId,
      };
   },
   [ServerActionTypes.CLOUD_MARK_COMPLETED]: (item, profile, periodMap, studyId) =>
      expenseCompletionFormatter(item, profile, periodMap, studyId, CLOUD_CONFIG),

   [ServerActionTypes.SUPPLIES_MARK_COMPLETED]: (item, profile, periodMap, studyId) =>
      expenseCompletionFormatter(item, profile, periodMap, studyId, SUPPLIES_CONFIG),

   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.TIME_SURVEY_SUBMIT]: (item, profile, periodMap, studyId) => {
      const period = periodMap[item.speriodId];
      const text = 'Employee Time section is ready to be reviewed and marked complete';
      const to = {name: EMPLOYEE_CONFIG.routeName, params: {periodId: period.id}};
      const buttonText = 'Go';
      return {
         action: ActionItemTypes.TIME_SURVEY_SUBMIT,
         data: item,
         text,
         to,
         buttonText,
         studyId,
      };
   },

   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.CONTRACTOR_SUBMIT]: (item, profile, periodMap, studyId) => {
      const period = periodMap[item.speriodId];

      if (item.uid === null) {
         return {
            action: ActionItemTypes.CONTRACTOR_ASSIGNMENTS,
            data: item,
            text: 'Some contract researchers need assignments for contract research time entry',
            to: {name: 'contractors-table', params: {periodId: period.id}},
            buttonText: 'Review Assignments',
            studyId,
         };
      }

      const userName = `${item.firstName} ${item.lastName}`;
      const text = `${userName} - All assigned ${period.label} contract researchers have valid data`;
      const to = {name: 'contractors-period-review', params: {periodId: period.id}};
      const buttonText = 'Review';
      return {
         action: ActionItemTypes.CONTRACTOR_DATA_FINISHED,
         data: item,
         text,
         to,
         buttonText,
         studyId,
      };
   },
   [ServerActionTypes.CLOUD_SUBMIT]: (item, profile, periodMap, studyId) =>
      expenseSubmitFormatter(item, periodMap, studyId, {
         ...CLOUD_CONFIG,
         routeName: 'cloud-table',
      }),

   [ServerActionTypes.SUPPLIES_SUBMIT]: (item, profile, periodMap, studyId) =>
      expenseSubmitFormatter(item, periodMap, studyId, {
         ...SUPPLIES_CONFIG,
         routeName: 'supplies-table',
      }),

   [ServerActionTypes.PROJECT_QUESTIONNAIRE_ANSWERS]: (item, profile, periodMap, studyId) => {
      let text, routeName, action, buttonText;

      if (item.completed !== null) {
         text = item.title;
         routeName = 'customer-project-questionnaire-review';
         action = ActionItemTypes.PROJECT_QUESTIONNAIRE_FINISHED;
         buttonText = 'Review';
      } else if (item.unanswered.length === 0) {
         text = `"${item.title}" is ready to submit`;
         routeName = 'customer-project-questionnaire-review';
         action = ActionItemTypes.PROJECT_QUESTIONNAIRE_SUBMIT;
         buttonText = 'Go';
      } else {
         text = `${item.unanswered.length}/${item.count} questions on "${item.title}" remaining`;
         routeName = 'customer-project-questionnaire-complete';
         action = ActionItemTypes.PROJECT_QUESTIONNAIRE_ANSWERS;
         buttonText = item.count === item.unanswered.length ? 'Start' : 'Go';
      }

      const to = {
         name: routeName,
         params: {id: profile.companyId, questionnaireId: item.id},
      };

      return {action, data: item, text, to, buttonText, studyId};
   },

   [ServerActionTypes.COMPANY_QUESTIONNAIRE_ANSWERS]: (item, profile, periodMap, studyId) => {
      let text, routeName, action, buttonText;

      if (item.completed !== null) {
         text = item.title;
         routeName = 'customer-company-questionnaire-review';
         action = ActionItemTypes.COMPANY_QUESTIONNAIRE_FINISHED;
         buttonText = 'Review';
      } else if (item.unanswered.length === 0) {
         text = `"${item.title}" is ready to submit`;
         routeName = 'customer-company-questionnaire-review';
         action = ActionItemTypes.COMPANY_QUESTIONNAIRE_SUBMIT;
         buttonText = 'Go';
      } else {
         text = `${item.unanswered.length}/${item.count} questions on "${item.title}" remaining`;
         routeName = 'customer-company-questionnaire-complete';
         action = ActionItemTypes.COMPANY_QUESTIONNAIRE_ANSWERS;
         buttonText = item.count === item.unanswered.length ? 'Start' : 'Go';
      }

      const to = {
         name: routeName,
         params: {id: profile.companyId, questionnaireId: item.id},
      };

      return {action, data: item, text, to, buttonText, studyId};
   },

   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.PROFILE_QUESTIONNAIRE_ANSWERS]: (item, profile, periodMap, studyId) => {
      let text, action, buttonText;

      if (item.unanswered.length === 0) {
         text = `Your Onboard profile is complete`;
         action = ActionItemTypes.PROFILE_QUESTIONNAIRE_FINISHED;
         buttonText = 'Go';
      } else {
         text = `${item.unanswered.length} questions in your Onboard profile need your response`;
         action = ActionItemTypes.PROFILE_QUESTIONNAIRE_ANSWERS;
         buttonText = item.completed === 0 ? 'Start' : 'Go';
      }

      const to = {name: 'profile', query: {section: 'about'}};

      return {action, data: item, text, to, buttonText, studyId};
   },

   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.GROSS_RECEIPTS_MISSING]: (item, profile, periodMap, studyId) => {
      const incomplete = item.units - item.completed;

      let action, text, to, buttonText;
      if (incomplete === 0) {
         action = ActionItemTypes.GROSS_RECEIPTS_FINISHED;
         text = `All tax periods have gross receipts information`;
         to = {name: 'study-setup', params: {shouldOpenTaxReturnModal: true}};
         buttonText = 'Review';
      } else {
         action = ActionItemTypes.GROSS_RECEIPTS_MISSING;

         to = {name: 'study-setup', params: {shouldOpenTaxReturnModal: true}};
         buttonText = 'Go';
         text = `${incomplete}/${item.units} tax periods need gross receipts information`;
      }

      return {action, data: item, text, to, buttonText, studyId};
   },

   /* eslint-disable-next-line no-unused-vars */
   [ServerActionTypes.PAYROLL_DATA]: (item, profile, periodMap, studyId) => {
      let action, text, to, buttonText;
      const period = periodMap[item.speriodId];

      if (item.missing === 0) {
         action = ActionItemTypes.PAYROLL_DATA_FINISHED;
         text = `All employees have taxable wages entered for ${period.label}`;
         to = {name: 'employees-table'};
         buttonText = 'Review';
      } else {
         action = ActionItemTypes.PAYROLL_DATA_MISSING;
         to = {
            name: EMPLOYEE_CONFIG.routeName,
            params: {periodId: item.speriodId},
         };
         buttonText = 'Go';
         text = `${item.missing}/${item.total} ${
            item.missing === 1 ? 'employee need' : 'employees needs'
         } taxable wages entered for ${period.label}`;
      }

      return {action, data: item, text, to, buttonText, studyId};
   },
};

/** Computes the supplies section status, returning an ActionItem */
const suppliesState = (data, studyId) => {
   if (data.completedUnderPriorRules) return undefined;
   if (data.units === 1) {
      // With only 1 unit, we are at the declaration stage
      if (data.completed === 0) {
         // No declaration has been made
         return {
            action: ActionItemTypes.SUPPLIES_DECLARATION,
            buttonText: 'Start',
            text: 'Determine whether or not your company used supplies',
            to: {name: 'supplies-table'},
            studyId,
         };
      } else {
         // Declaration was "no"
         return {
            action: ActionItemTypes.SUPPLIES_COMPLETE,
            buttonText: 'Review',
            text: 'Your company did not use qualified supplies',
            to: {name: 'supplies-table'},
            studyId,
         };
      }
   } else if (data.completed === 1 && data.units === 2 && data.actions.length == 0) {
      // Declaration was "Yes"
      return {
         action: ActionItemTypes.SUPPLIES_DECLARATION,
         buttonText: 'Start',
         text: 'The Supplies section is ready for your attention',
         to: {name: 'supplies-table'},
         studyId,
      };
   }
};

/** Computes the contractor section status, returning an action item */
const contractorStatus = (data, isSME, studyId) => {
   let action, text;
   let to = {name: 'contractors-table'};
   let buttonText = 'Go';

   if (isSME) {
      if (data.percentage === null || data.percentage < 100) {
         action = ActionItemTypes.CONTRACTOR_INCOMPLETE;
         text = 'The Contract Research section needs your attention.';
      } else {
         action = ActionItemTypes.CONTRACTOR_COMPLETE;
         text = 'The Contract Research section is complete.';
      }
   } else {
      if (data.units === 1) {
         if (data.completed === 0) {
            action = ActionItemTypes.CONTRACTOR_EMPTY;
            text = 'Determine whether or not your company used contract research';
            buttonText = 'Start';
         } else if (data.completed === 1) {
            action = ActionItemTypes.CONTRACTOR_COMPLETE;
            text = 'Your company did not use qualified contract research';
            buttonText = 'Review';
         }
      } else if (data.completed === 1 && data.units === 2 && data.actions.length == 0) {
         action = ActionItemTypes.CONTRACTOR_EMPTY;
         text = 'The Contract Research section is ready for your attention';
         buttonText = 'Start';
      }
   }
   if (action) {
      return {action, text, to, buttonText, studyId};
   }
};

const documentsActionItem = (studyId) => {
   return {
      action: ActionItemTypes.DOCUMENTS,
      text: 'Provide the requested documents.',
      to: {name: 'study-setup'},
      buttonText: 'Go',
      studyId,
   };
};

/** Computes the cloud computing section status, returning an action item */
const cloudStatus = (data, studyId) => {
   if (data.completedUnderPriorRules) return undefined;
   if (data.units === 1) {
      // With only 1 unit, we are at the declaration stage
      if (data.completed === 0) {
         // No declaration has been made
         return {
            action: ActionItemTypes.CLOUD_EMPTY,
            buttonText: 'Start',
            text: 'Determine whether or not your company used cloud computing',
            to: {name: 'cloud-table'},
            studyId,
         };
      } else {
         // Declaration was "no"
         return {
            action: ActionItemTypes.CLOUD_COMPLETE,
            buttonText: 'Review',
            text: 'Your company did not use qualified cloud computing resources',
            to: {name: 'cloud-table'},
            studyId,
         };
      }
   } else if (data.completed === 1 && data.units === 2 && data.actions.length == 0) {
      // Declaration was "Yes"
      return {
         action: ActionItemTypes.CLOUD_EMPTY,
         buttonText: 'Start',
         text: 'The Cloud Computing section is ready for your attention',
         to: {name: 'cloud-table'},
         studyId,
      };
   }
};

/** Sort actions by action type, then period ascending */
const sortActions = (periodMap) => (a, b) => {
   const priorityA = ACTION_PRIORITY[a.action];
   const priorityB = ACTION_PRIORITY[b.action];

   if (priorityA < priorityB) {
      return -1;
   } else if (priorityB < priorityA) {
      return 1;
   }

   // For items of the same type, sort by period date
   if (a.speriodId && b.speriodId) {
      const aPeriod = periodMap[a.speriodId];
      const bPeriod = periodMap[b.speriodId];
      if (aPeriod.isBefore(bPeriod)) {
         return -1;
      } else if (bPeriod.isBefore(aPeriod)) {
         return 1;
      }
   }

   return 0;
};

const studyKey = (studyId) => `study:${studyId}`;

const getters = {
   /** Has progress data been loaded this session? */
   loaded: (state) => state.loaded,

   activeStudyKey: (state, getters, rootState, rootGetters) => {
      const activeStudyId = rootGetters['companies/activeStudyId'];
      return activeStudyId ? studyKey(activeStudyId) : null;
   },

   activeStudyData: (state, getters) => {
      const key = getters.activeStudyKey;
      return key && state.rawData ? state.rawData.components[key] : null;
   },

   /** Returns an object containing the progress percentage for each progress category */
   studyProgress: (state, getters, rootState, rootGetters) => (studyId) => {
      const progress = {
         total: null,
         profile: null,
         company: null,
         project: null,
         timeSurvey: null,
         supplies: null,
         contractors: null,
         cloud: null,
         taxReturns: null,
      };

      const key = studyKey(studyId);
      const studyData = state.rawData ? state.rawData.components[key] : null;

      if (studyData) {
         progress.total = studyData.percentage || 0;
         progress.profile = studyData.components.profileQuestionnaire
            ? studyData.components.profileQuestionnaire.percentage
            : null;
         progress.company = studyData.components.companyQuestionnaire
            ? studyData.components.companyQuestionnaire.percentage
            : null;
         progress.project = studyData.components.projectQuestionnaire
            ? studyData.components.projectQuestionnaire.percentage
            : null;
         progress.timeSurvey = studyData.components.timeSurvey
            ? studyData.components.timeSurvey.percentage
            : null;
         progress.supplies = studyData.components.supplies
            ? studyData.components.supplies.percentage
            : null;
         progress.contractors = studyData.components.contractor
            ? studyData.components.contractor.percentage
            : null;
         progress.cloud = studyData.components.cloudComputing
            ? studyData.components.cloudComputing.percentage
            : null;
         progress.taxReturns = studyData.components.taxReturns
            ? studyData.components.taxReturns.percentage
            : null;

         if (rootGetters.isSME) {
            if (getters.hasUncompletedCloudAssignment && getters.hasCloudAssignment) {
               progress.cloud = progress.cloud || 0;
            }

            if (getters.hasUncompletedSuppliesAssignment && getters.hasSuppliesAssignment) {
               progress.supplies = progress.supplies || 0;
            }

            if (getters.hasContractorAssignment) {
               progress.contractors = progress.contractors || 0;
            }
         }
      }

      return progress;
   },

   activeStudyProgress: (state, getters, rootState, rootGetters) => {
      return rootGetters['companies/activeStudyId']
         ? getters.studyProgress(rootGetters['companies/activeStudyId'])
         : null;
   },

   /** Compute the average progress percentage across all studies */
   totalProgress: (state, getters) => {
      const progress = {
         total: state.rawData ? state.rawData.percentage : null,
         profile: null,
         company: null,
         project: null,
         timeSurvey: null,
         supplies: null,
         contractors: null,
         cloud: null,
         taxReturns: null,
      };

      if (!state.rawData) {
         return progress;
      }

      // Sum component progress percentages
      for (let sKey in state.rawData.components) {
         const studyId = sKey.split(':')[1];
         const studyProgress = getters.studyProgress(studyId);

         for (let pKey in progress) {
            if (pKey === 'total') continue;

            progress[pKey] += studyProgress[pKey];
         }
      }

      // Divide by study count
      const studyCount = Object.keys(state.rawData.components).length;
      for (let pKey in progress) {
         if (pKey === 'total') continue;

         progress[pKey] = Math.floor(progress[pKey] / studyCount);
      }

      return progress;
   },

   /**  Formatted actions for each category */
   actionItems: (state, getters, rootState, rootGetters) => {
      let companyActionItems = [];
      let projectActionItems = [];
      let timeSurveyActionItems = [];
      let profileActionItems = [];
      let suppliesActionItems = [];
      let contractorActionItems = [];
      let cloudActionItems = [];
      let documentsActionItems = [];
      let studySetupActionItems = [];

      if (state.rawData) {
         Object.keys(state.rawData.components).forEach((studyKey) => {
            const studyData = state.rawData.components[studyKey];
            const studyId = studyKey.split(':')[1];

            const profile = state.rawData.user;

            const _mapActionItems = (componentName, fallback) => {
               const component = studyData.components[componentName];
               if (!component && typeof fallback !== 'undefined') {
                  return fallback;
               }
               const periodMap = rootGetters['companies/studyPeriodMap'];
               return component.actions
                  .map((item) =>
                     ActionItemFormatters[item.action](item, profile, periodMap, studyId)
                  )
                  .sort(sortActions(periodMap));
            };

            const anyPeriodConfigured = rootGetters['companies/studyMap'][studyId].periods.some(
               (period) => rootGetters['userInfo'].isTimesurveyConfigured[period.id]
            );

            if (!anyPeriodConfigured) {
               // Study unconfigured. Add documents action item, then skip all other actions.
               documentsActionItems.push(documentsActionItem(studyId));
               return;
            }

            companyActionItems = [
               ...companyActionItems,
               ..._mapActionItems('companyQuestionnaire', []),
            ];
            projectActionItems = [
               ...projectActionItems,
               ..._mapActionItems('projectQuestionnaire', []),
            ];
            timeSurveyActionItems = [...timeSurveyActionItems, ..._mapActionItems('timeSurvey')];
            cloudActionItems = [...cloudActionItems, ..._mapActionItems('cloudComputing')];
            suppliesActionItems = [...suppliesActionItems, ..._mapActionItems('supplies')];

            contractorActionItems = [...contractorActionItems, ..._mapActionItems('contractor')];
            profileActionItems = [
               ...profileActionItems,
               ..._mapActionItems('profileQuestionnaire'),
            ];

            if (!rootGetters.isSME) {
               studySetupActionItems = [...studySetupActionItems, ..._mapActionItems('taxReturns')];
            }

            if (studyData.components.supplies) {
               const state = suppliesState(studyData.components.supplies, studyId);
               if (state) {
                  suppliesActionItems.push(state);
               }
            }

            if (
               studyData.components.contractor &&
               !(rootGetters.isSME && !getters.hasContractorAssignment) // exclude the case where an SME has no assignment
            ) {
               const status = contractorStatus(
                  studyData.components.contractor,
                  rootGetters.isSME,
                  studyId
               );
               if (status) {
                  contractorActionItems.push(status);
               }
            }

            if (studyData.components.cloudComputing) {
               const status = cloudStatus(studyData.components.cloudComputing, studyId);
               if (status) {
                  cloudActionItems.push(status);
               }
            }
         });
      }

      return {
         company: companyActionItems,
         project: projectActionItems,
         timeSurvey: timeSurveyActionItems,
         profile: profileActionItems,
         supplies: suppliesActionItems,
         contractors: contractorActionItems,
         cloud: cloudActionItems,
         documents: documentsActionItems,
         studySetup: studySetupActionItems,
      };
   },

   activeStudyActionItems: (state, getters, rootState, rootGetters) => {
      const actionItems = getters.actionItems;
      const activeStudyId = rootGetters['companies/activeStudyId'];

      if (!activeStudyId) {
         return null;
      }

      return Object.entries(actionItems).reduce((obj, [key, actions]) => {
         obj[key] = actions.filter((action) => +action.studyId === +activeStudyId);
         return obj;
      }, {});
   },

   /** Priority action items for the current user */
   priorityActionItems: (state, getters, rootState, rootGetters) => {
      let assignmentsActionItemExists = false;
      let constractorAssignmentsActionItemExists = false;
      let constractorInvoiceAssignmentsActionItemExists = false;
      let questionnaireSectionReadyActionItemExists = false;

      if (getters.activeStudyData === null) {
         return [];
      }

      const userId = state.rawData.user.id;
      const actionItems = getters['actionItems'];
      const periodMap = rootGetters['companies/studyPeriodMap'];

      return [
         ...actionItems.company,
         ...actionItems.project,
         ...actionItems.timeSurvey,
         ...actionItems.supplies,
         ...actionItems.contractors,
         ...actionItems.cloud,
         ...actionItems.profile,
         ...actionItems.documents,
         ...actionItems.studySetup,
      ]
         .filter((item) => {
            switch (item.action) {
               case ActionItemTypes.TIME_SURVEY_ASSIGNMENTS:
                  // Include the first instance of TIME_SURVEY_ASSIGNMENTS
                  if (assignmentsActionItemExists) return false;
                  assignmentsActionItemExists = true;
                  return true;

               case ActionItemTypes.TIME_SURVEY_DATA:
                  // Include incomplete TIME_SURVEY_DATA assigned to the current user or unassigned
                  return (
                     (item.data.uid === null || item.data.uid === userId) &&
                     item.data.completed !== item.data.units
                  );

               case ActionItemTypes.CONTRACTOR_ASSIGNMENTS:
                  // Include the first instance of CONTRACTOR_ASSIGNMENTS
                  if (constractorAssignmentsActionItemExists) return false;
                  constractorAssignmentsActionItemExists = true;
                  return true;

               case ActionItemTypes.CONTRACTOR_DATA:
                  // Include incomplete CONTRACTOR_DATA assigned to the current user or unassigned
                  return (
                     (item.data.uid === null || item.data.uid === userId) &&
                     item.data.completed !== item.data.units
                  );

               case ActionItemTypes.CONTRACTOR_INVOICE_ASSIGNMENTS:
                  // Include the first instance of CONTRACTOR_ASSIGNMENTS
                  if (constractorInvoiceAssignmentsActionItemExists) return false;
                  constractorInvoiceAssignmentsActionItemExists = true;
                  return true;

               case ActionItemTypes.CONTRACTOR_INVOICE_DATA:
                  // Include incomplete CONTRACTOR_INVOICE_DATA assigned to the current user or unassigned
                  return (
                     (item.data.uid === null || item.data.uid === userId) &&
                     item.data.filledPayments !== item.data.units
                  );

               case ActionItemTypes.TIME_SURVEY_MARK_COMPLETED:
                  return item.data.uid === userId;

               case ActionItemTypes.CONTRACTOR_MARK_COMPLETED:
                  return item.data.uid === userId;

               case ActionItemTypes.PROJECT_QUESTIONNAIRE_ANSWERS:
               case ActionItemTypes.COMPANY_QUESTIONNAIRE_ANSWERS:
                  return (
                     item.data.unanswered.length > 0 &&
                     (item.data.assigneeId === null || item.data.assigneeId === userId)
                  );
               case ActionItemTypes.PROFILE_QUESTIONNAIRE_ANSWERS:
                  return item.data.userId === userId;

               case ActionItemTypes.CLOUD_DATA:
               case ActionItemTypes.SUPPLIES_DATA:
                  return (
                     (item.data.uid === null || item.data.uid === userId) &&
                     item.data.completed !== item.data.units
                  );

               case ActionItemTypes.CLOUD_MARK_COMPLETED:
               case ActionItemTypes.SUPPLIES_MARK_COMPLETED:
                  return item.data.uid === userId;

               case ActionItemTypes.QUESTIONNAIRE_SECTION_READY:
                  // Server emits this once per bucket (company/project) that has
                  // questionnaires, so it can appear twice with identical text --
                  // only surface the first instance here.
                  if (questionnaireSectionReadyActionItemExists) return false;
                  questionnaireSectionReadyActionItemExists = true;
                  return true;

               case ActionItemTypes.CLOUD_EMPTY:
               case ActionItemTypes.CLOUD_UPLOAD:
               case ActionItemTypes.CLOUD_ASSIGNMENTS:
               case ActionItemTypes.CLOUD_SUBMIT:
               case ActionItemTypes.SUPPLIES_DECLARATION:
               case ActionItemTypes.SUPPLIES_UPLOAD:
               case ActionItemTypes.SUPPLIES_ASSIGNMENTS:
               case ActionItemTypes.SUPPLIES_SUBMIT:
               case ActionItemTypes.TIME_SURVEY_SUBMIT:
               case ActionItemTypes.PROJECT_QUESTIONNAIRE_SUBMIT:
               case ActionItemTypes.COMPANY_QUESTIONNAIRE_SUBMIT:
               case ActionItemTypes.CONTRACTOR_EMPTY:
               case ActionItemTypes.CONTRACTOR_INCOMPLETE:
               case ActionItemTypes.CONTRACTOR_SUBMIT:
               case ActionItemTypes.DOCUMENTS:
               case ActionItemTypes.GROSS_RECEIPTS_MISSING:
               case ActionItemTypes.PAYROLL_DATA_MISSING:
                  return true;

               default:
                  return false;
            }
         })
         .sort(sortActions(periodMap));
   },

   /** Priority action items used only by the dashboard attention card */
   attentionCardActionItems: (state, getters, rootState, rootGetters) => {
      const hasContractorPaymentsPermission =
         rootGetters['users/currentUserPermissionFlags']?.contractorPayments ?? true;

      return getters.priorityActionItems.filter((item) => {
         if (item.action !== ActionItemTypes.CONTRACTOR_INVOICE_DATA) {
            return true;
         }

         return hasContractorPaymentsPermission;
      });
   },

   /** All profile action items */
   profileActionItems: (state, getters) => {
      if (getters.activeStudyData === null) {
         return [];
      }

      return getters['actionItems'].profile;
   },

   /** Is the current user's profile questionnaire complete? */
   isProfileQuestionnaireComplete: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig || getters.activeStudyData === null) {
         return true;
      }

      const userId = state.rawData.user.id;
      return getters.profileActionItems.some(
         (item) =>
            item.action === ActionItemTypes.PROFILE_QUESTIONNAIRE_FINISHED &&
            item.data.userId === userId
      );
   },

   /** Incomplete action items related to time surveys */
   timeSurveyActionItems: (state, getters) => {
      let assignmentsActionItemExists = false;

      if (getters.activeStudyData === null) {
         return [];
      }

      // Scoped to the active study only -- deduping across every study a
      // company has would let an unrelated study's assignments banner
      // silently suppress this study's own banner (see TIME_SURVEY_ASSIGNMENTS
      // below).
      return getters.activeStudyActionItems.timeSurvey.filter((item) => {
         switch (item.action) {
            case ActionItemTypes.TIME_SURVEY_ASSIGNMENTS:
               if (assignmentsActionItemExists) return false;
               assignmentsActionItemExists = true;
               return true;
            case ActionItemTypes.TIME_SURVEY_DATA:
            case ActionItemTypes.PAYROLL_DATA_MISSING:
            case ActionItemTypes.TIME_SURVEY_SUBMIT:
            case ActionItemTypes.TIME_SURVEY_MARK_COMPLETED:
               return true;
            default:
               return false;
         }
      });
   },

   /** Completed action items related to time surveys */
   timeSurveyCompletedItems: (state, getters) => {
      if (getters.activeStudyData === null) {
         return [];
      }

      return getters['actionItems'].timeSurvey.filter((item) => {
         return (
            item.action === ActionItemTypes.TIME_SURVEY_DATA_FINISHED ||
            item.action === ActionItemTypes.PAYROLL_DATA_FINISHED
         );
      });
   },

   /** Incomplete action items related to project questionnaires */
   projectActionItems: (state, getters) => {
      return getters['actionItems'].project.filter((item) => {
         switch (item.action) {
            case ActionItemTypes.PROJECT_QUESTIONNAIRE_ANSWERS:
            case ActionItemTypes.PROJECT_QUESTIONNAIRE_SUBMIT:
            case ActionItemTypes.QUESTIONNAIRE_SECTION_READY:
            case ActionItemTypes.QUESTIONNAIRE_WAITING_ON_COMPANY:
               return true;
            default:
               return false;
         }
      });
   },

   /** Completed action items related to project questionnaires */
   projectCompletedItems: (state, getters) => {
      return getters['actionItems'].project.filter((item) => {
         return item.action === ActionItemTypes.PROJECT_QUESTIONNAIRE_FINISHED;
      });
   },

   /** Incomplete action items related to company questionnaires */
   companyActionItems: (state, getters) => {
      return getters['actionItems'].company.filter((item) => {
         switch (item.action) {
            case ActionItemTypes.COMPANY_QUESTIONNAIRE_ANSWERS:
            case ActionItemTypes.COMPANY_QUESTIONNAIRE_SUBMIT:
            case ActionItemTypes.QUESTIONNAIRE_SECTION_READY:
            case ActionItemTypes.QUESTIONNAIRE_WAITING_ON_PROJECT:
               return true;
            default:
               return false;
         }
      });
   },

   /** Completed action items related to company questionnaires */
   companyCompletedItems: (state, getters) => {
      return getters['actionItems'].company.filter((item) => {
         return item.action === ActionItemTypes.COMPANY_QUESTIONNAIRE_FINISHED;
      });
   },

   /** Incomplete action items related to supplies */
   suppliesActionItems: (state, getters) => {
      return getters['actionItems'].supplies.filter((item) => {
         switch (item.action) {
            case ActionItemTypes.SUPPLIES_DECLARATION:
            case ActionItemTypes.SUPPLIES_UPLOAD:
            case ActionItemTypes.SUPPLIES_DATA:
            case ActionItemTypes.SUPPLIES_SUBMIT:
            case ActionItemTypes.SUPPLIES_MARK_COMPLETED:
            case ActionItemTypes.SUPPLIES_ASSIGNMENTS:
               return true;
            default:
               return false;
         }
      });
   },

   /** Completed action items related to supplies */
   suppliesCompletedItems: (state, getters) => {
      if (getters.activeStudyData === null) {
         return [];
      }
      return getters['actionItems'].supplies.filter((item) => {
         return (
            item.action === ActionItemTypes.SUPPLIES_DATA_FINISHED ||
            item.action === ActionItemTypes.SUPPLIES_COMPLETE
         );
      });
   },

   /** Incomplete actions for the contractor section */
   contractorActionItems: (state, getters) => {
      let assignmentsActionItemExists = false;
      let invoiceAssignmentsActionItemExists = false;

      if (getters.activeStudyData === null) {
         return [];
      }

      // Scoped to the active study only -- see timeSurveyActionItems above for
      // why deduping across every study a company has is wrong here.
      return getters.activeStudyActionItems.contractors.filter((item) => {
         switch (item.action) {
            case ActionItemTypes.CONTRACTOR_ASSIGNMENTS:
               if (assignmentsActionItemExists) return false;
               assignmentsActionItemExists = true;
               return true;
            case ActionItemTypes.CONTRACTOR_INVOICE_ASSIGNMENTS:
               if (invoiceAssignmentsActionItemExists) return false;
               invoiceAssignmentsActionItemExists = true;
               return true;
            case ActionItemTypes.CONTRACTOR_EMPTY:
            case ActionItemTypes.CONTRACTOR_INCOMPLETE:
            case ActionItemTypes.CONTRACTOR_SUBMIT:
            case ActionItemTypes.CONTRACTOR_INVOICE_DATA:
            case ActionItemTypes.CONTRACTOR_DATA:
            case ActionItemTypes.CONTRACTOR_MARK_COMPLETED:
               return true;
            default:
               return false;
         }
      });
   },

   /** Completed actions for the contractor section */
   contractorCompletedItems: (state, getters) => {
      if (getters.activeStudyData === null) {
         return [];
      }

      return getters.actionItems.contractors.filter((item) => {
         return (
            item.action === ActionItemTypes.CONTRACTOR_COMPLETE ||
            item.action === ActionItemTypes.CONTRACTOR_DATA_FINISHED ||
            item.action === ActionItemTypes.CONTRACTOR_INVOICE_DATA_FINISHED
         );
      });
   },

   /** Incomplete actions for the cloud computing section */
   cloudActionItems: (state, getters) => {
      return getters.actionItems.cloud.filter((item) => {
         switch (item.action) {
            case ActionItemTypes.CLOUD_EMPTY:
            case ActionItemTypes.CLOUD_UPLOAD:
            case ActionItemTypes.CLOUD_DATA:
            case ActionItemTypes.CLOUD_SUBMIT:
            case ActionItemTypes.CLOUD_MARK_COMPLETED:
            case ActionItemTypes.CLOUD_ASSIGNMENTS:
               return true;
            default:
               return false;
         }
      });
   },

   /** Completed actions for the cloud computing section */
   cloudCompletedItems: (state, getters) => {
      if (getters.activeStudyData === null) {
         return [];
      }
      return getters.actionItems.cloud.filter((item) => {
         return (
            item.action === ActionItemTypes.CLOUD_DATA_FINISHED ||
            item.action === ActionItemTypes.CLOUD_COMPLETE
         );
      });
   },

   studySetupActionItems: (state, getters) => {
      return getters.actionItems.studySetup.filter((item) => {
         return item.action === ActionItemTypes.GROSS_RECEIPTS_MISSING;
      });
   },

   studySetupCompletedItems: (state, getters) => {
      return getters.actionItems.studySetup.filter((item) => {
         return item.action === ActionItemTypes.GROSS_RECEIPTS_FINISHED;
      });
   },

   /** Does the current user have any questionnaire assignments? */
   hasQuestionnaireAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig) {
         return false;
      }

      const anyCompanyQs = getters.activeStudyActionItems.company.length > 0;
      const anyProjectQs = getters.activeStudyActionItems.project.length > 0;

      return anyCompanyQs || anyProjectQs;
   },

   /** Does the current user have any company questionnaire assignments? */
   hasCompanyQuestionnaireAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig) {
         return false;
      }

      return getters.activeStudyActionItems.company.length > 0;
   },

   /** Does the current user have any company questionnaire assignments? */
   hasProjectQuestionnaireAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig) {
         return false;
      }

      return getters.activeStudyActionItems.project.length > 0;
   },

   /** Does the current user have any contractor permissions? */
   hasContractorAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig) {
         return false;
      }

      const activeStudy = rootGetters['companies/activeStudy'];
      const userId = rootGetters.profile.id;
      if (activeStudy && activeStudy.contractorAssignments) {
         return activeStudy.contractorAssignments.some(
            (assignment) => assignment.assigneeId === userId
         );
      }

      return false;
   },

   hasSuppliesAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig || state.rawData === null) {
         return false;
      }

      return rootGetters['supplies/hasSectionAssignment'];
   },

   hasUncompletedSuppliesAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig || state.rawData === null) {
         return false;
      }

      return rootGetters['supplies/hasUncompletedAssignment'];
   },

   /** Does the current user have any time survey assignments? */
   hasTimeSurveyAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig || state.rawData === null) {
         return false;
      }

      return rootGetters['employees/hasSectionAssignment'];
   },

   hasUncompletedCloudAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig || state.rawData === null) {
         return false;
      }

      return rootGetters['cloud/hasUncompletedAssignment'];
   },

   hasCloudAssignment: (state, getters, rootState, rootGetters) => {
      if (rootGetters.isRndig || state.rawData === null) {
         return false;
      }

      return rootGetters['cloud/hasSectionAssignment'];
   },

   studySetupData: (state) => {
      return state.studySetupData;
   },
};

const mutations = {
   /** Store the incoming data */
   setData(state, {data}) {
      state.rawData = data;
      state.loaded = true;
   },

   setStudySetupData(state, {data}) {
      state.studySetupData = data ? {...data} : null;
   },
};

const actions = {
   /** Load progress data for a company */
   async loadProgress({commit}, {companyId, allStudies = false}) {
      const http = allStudies ? this._vm.$httpNoStudy : this._vm.$http;
      const data = (await http.get(`/api/company/${companyId}/progress`)).data;
      commit('setData', {
         data,
      });
   },

   async loadStudySetupProgress({commit}, {studyId}) {
      const data = (await this._vm.$http.get(`/api/study/${studyId}/dashboard`)).data;
      commit('setStudySetupData', {
         data,
      });
   },

   async toggleStudySetupCompletion({rootGetters, dispatch}, {studyId, completed}) {
      const currentCompany = rootGetters['companies/currentCompany'];
      await this._vm.$http.post(`api/study/${studyId}/study_setup_completed`, {completed});
      await dispatch(
         'companies/loadCompany',
         {companyId: currentCompany.id, force: true},
         {root: true}
      );
   },
};

export default {
   namespaced: true,
   state,
   getters,
   mutations,
   actions,
};
